import { Button } from "@/components/shadcn/button";
import { Controller, Control, type FieldValues } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import ModalCodeEditor from "../Modals/ModalCodeEditor";
import { Maximize2 } from "lucide-react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
import "prismjs/components/prism-python";
import "prismjs/components/prism-json";
import "prism-themes/themes/prism-one-light.css";
import { NoxusFormLabel } from "./NoxusFormLabel";

import { useDisclosure } from "@/utils/useDisclosure";
import { cn } from "@/lib/utils";
interface ICodeField {
  name: string;
  placeholder?: string;
  label: string;
  isDisabled?: boolean;
  inputValues?: string[];
  outputValues?: string[];
  helperText?: string;
  language?: string;
  control?: Control<FieldValues>;
}

export const CodeInput = ({
  label,
  value,
  placeholder,
  isDisabled,
  onChange,
  onBlur,
  isExpanded,
  name,
  helperText,
  language = "python",
}: {
  label?: string;
  value: unknown;
  placeholder?: string;
  isDisabled?: boolean;
  onChange: any;
  onBlur?: any;
  isExpanded?: boolean;
  name?: string;
  helperText?: string;
  language?: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const containerRef = useRef(null);

  // Run viewers seed this with the node's stored input, which for JSON-typed
  // inputs is an object/array — render it pretty-printed instead of crashing.
  const stringValue = useMemo(() => {
    if (typeof value === "string" || value == null) return value ?? undefined;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);

  const lineNumbers = useMemo(
    () =>
      (stringValue || placeholder || "")
        .split("\n")
        .map((_, index) => index + 1)
        .join("\n"),
    [stringValue, placeholder]
  );

  useEffect(() => {
    if (containerRef.current) {
      // Scroll to the bottom whenever the value changes
      (containerRef.current as HTMLDivElement).scrollTop = (
        containerRef.current as HTMLDivElement
      ).scrollHeight;
    }
  }, [lineNumbers]);

  const highlightCode = (code: string) => {
    return Prism.highlight(
      code,
      Prism.languages[language] || Prism.languages.python,
      language
    );
  };

  return (
    <div className="flex flex-col w-full flex-1 max-h-full" onBlur={onBlur}>
      {label && !isExpanded && <NoxusFormLabel label={label} name={name} />}
      <div
        className="flex flex-col flex-1 text-xs leading-5 bg-background border rounded-md overflow-hidden relative max-h-full"
        style={{
          fontFamily: '"Fira Code", "Fira Mono", monospace',
        }}
      >
        {/* Toolbar */}
        {!isExpanded && (
          <div className="px-4 py-1 border-b justify-end flex">
            <Button
              variant="ghost"
              size="iconsm"
              onClick={onOpen}
              isDisabled={isDisabled}
              aria-label={"Expand"}
            >
              <Maximize2 className="size-4" strokeWidth={1.25} />
            </Button>
          </div>
        )}
        {/* Body */}

        <div
          className={cn(
            "p-4 overflow-auto relative flex-1",
            isExpanded ? "max-h-full" : "max-h-100"
          )}
          ref={containerRef}
        >
          <div
            className={cn("flex flex-1 h-min", isDisabled ? "opacity-50" : "")}
          >
            <div className="text-right min-w-8 p-0 pr-4 truncate text-primary h-min">
              <p className="whitespace-pre">{lineNumbers}</p>
            </div>
            {(stringValue === "" || stringValue === undefined) && (
              <p className="absolute top-4 left-12 text-muted-foreground whitespace-pre pointer-events-none">
                {placeholder}
              </p>
            )}
            <Editor
              value={stringValue || ""}
              disabled={isDisabled}
              onValueChange={onChange}
              highlight={highlightCode}
              textareaClassName="code-editor"
              style={{
                minWidth: "max-content",
                minHeight: "6rem",
                flex: 1,
                whiteSpace: "pre-nowrap",
              }}
            />
          </div>
        </div>
      </div>

      {helperText != undefined && (
        <div className="mt-1.5 text-xs font-regular text-muted-foreground">
          {helperText}
        </div>
      )}

      {!isExpanded && isOpen && (
        <ModalCodeEditor
          isOpen={isOpen}
          onClose={onClose}
          value={stringValue || ""}
          placeholder={placeholder}
          onChange={onChange}
          isDisabled={isDisabled}
          label={label}
          language={language}
        />
      )}
    </div>
  );
};

export const CodeField = ({
  name,
  placeholder,
  label,
  isDisabled,
  inputValues,
  outputValues,
  helperText,
  language = "python",
  control,
}: ICodeField) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <CodeInput
          label={label}
          value={field.value}
          placeholder={placeholder}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isDisabled={isDisabled}
          name={name}
          helperText={helperText}
          language={language}
        />
      )}
    />
  );
};
